import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { moveFocusWithArrows } from '@/shared/lib/roving-focus';
import { avatarInitial } from '@/shared/lib/graphemes';
import { ROUTES } from '@/shared/constants/routes';
import { Icons } from '@/shared/constants/icons';
import { TRANSITION, motionSafe } from '@/shared/constants/motion';
import { useWorkspace } from '@/shared/hooks/useWorkspace';
import { useAccountState } from '@/shared/hooks/useAccountState';
import { useMyInvites } from '@/shared/hooks/useTeam';
import { useAuthStore } from '@/shared/stores/auth.store';
import type { Workspace } from '@/shared/stores/workspace.store';
import { AddBrandSheet } from './AddBrandSheet';

/** A stable colour per brand so the stacked avatars can be told apart. */
const BRAND_HUES = [268, 24, 168, 204, 340, 96];
function hueFor(ws: Workspace): number {
  let h = 0;
  for (const ch of ws.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return BRAND_HUES[h % BRAND_HUES.length] ?? 268;
}

function Avatar({ ws, size, ring }: { ws: Workspace; size: number; ring?: boolean }) {
  const cls = ['rounded-lg object-cover shrink-0 font-bold flex items-center justify-center', ring ? 'ring-2 ring-[var(--color-surface)]' : ''].join(' ');
  if (ws.logoUrl) return <img src={ws.logoUrl} alt="" className={cls} style={{ width: size, height: size }} />;
  return (
    <span className={cls} style={{ width: size, height: size, fontSize: size * 0.42, background: `hsl(${hueFor(ws)} 32% 88%)`, color: `hsl(${hueFor(ws)} 40% 30%)` }} aria-hidden="true">
      {avatarInitial(ws.name)}
    </span>
  );
}

const isMod = (e: KeyboardEvent): boolean => e.metaKey || e.ctrlKey;

/**
 * Brand switcher (blueprint C18 + M20). A workspace is one brand. The pill
 * shows stacked brand avatars, the current name (at every width) and the
 * brand count; the popover searches brands, lists them by name in "Your
 * brands" and "Shared with you", jumps with Ctrl/⌘ 1–9 while it is open, and
 * on Agency rolls up to all brands. Pending invitations sit at the top
 * (M-01). No shortcut is taken while it is closed: Ctrl/⌘ 1–9 and Ctrl B
 * stay the browser's (AL-48).
 *
 * The plan belongs to the account, not the brand (ADR-006), so it is shown
 * once, at the top of the popover, with the brands used of the plan's limit,
 * never on each brand row. Switching works for any account holding several
 * brands (Pro holds up to three); the all-brands roll-up is Agency's.
 *
 * TODO(api): per-brand "needs me" counts need a cross-workspace inbox query.
 */
export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reduced = useReducedMotion() ?? false;
  const { workspaces: unsorted, currentWorkspace, switchWorkspace } = useWorkspace();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // By name, so a brand keeps its number (AL-48); own brands first.
  const workspaces = useMemo(() => {
    const byName = [...unsorted].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return [...byName.filter((w) => w.ownerId === userId), ...byName.filter((w) => w.ownerId !== userId)];
  }, [unsorted, userId]);
  const { data: myInvites } = useMyInvites();
  const pendingInvites = myInvites ?? [];
  const [open, setOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const account = useAccountState();
  // Several brands to switch between (any plan), and the Agency roll-up.
  const multi = workspaces.length > 1;
  const agency = account.isAgency;
  const planLabel = t(`overview.plans.${account.plan}`);

  const handleSwitch = useCallback(
    (ws: Workspace) => {
      switchWorkspace(ws);
      setOpen(false);
      setQuery('');
    },
    [switchWorkspace],
  );

  const goAllBrands = useCallback((): void => {
    setOpen(false);
    setQuery('');
    void navigate(`${ROUTES.OVERVIEW}?scope=all`);
  }, [navigate]);

  // Close on outside click or Escape; focus the search on open.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Ctrl/⌘ 1–9 jump straight to a brand, only while the popover is open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (!isMod(e) || e.altKey) return;
      if (/^[1-9]$/.test(e.key) && workspaces.length > 1) {
        const ws = workspaces[Number(e.key) - 1];
        if (ws) {
          e.preventDefault();
          handleSwitch(ws);
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, workspaces, handleSwitch]);

  const hasShared = workspaces.some((w) => w.ownerId !== userId) && workspaces.some((w) => w.ownerId === userId);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? workspaces.filter((ws) => ws.name.toLowerCase().includes(q)) : workspaces;
  }, [workspaces, query]);

  if (!currentWorkspace) return null;

  const stacked = workspaces.slice(0, 3);

  return (
    <div ref={ref} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 h-11 ps-1.5 pe-2 rounded-[10px] bg-transparent border-none cursor-pointer hover:bg-[var(--color-surface-3)] max-w-[240px]"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${t('topbar.currentWorkspace', { name: currentWorkspace.name })}. ${t('topbar.switchWorkspace')}`}
      >
        <span className="flex items-center" aria-hidden="true">
          {multi
            ? stacked.map((ws, i) => (
                <span key={ws.id} className={i === 0 ? '' : '-ms-2'} style={{ zIndex: stacked.length - i, position: 'relative' }}>
                  <Avatar ws={ws} size={24} ring />
                </span>
              ))
            : <Avatar ws={currentWorkspace} size={28} />}
        </span>
        <span className="flex flex-col items-start min-w-0 text-start">
          <span className="text-ui font-semibold text-[var(--ink)] truncate max-w-[96px] sm:max-w-[150px] leading-tight" title={currentWorkspace.name}>
            {currentWorkspace.name}
          </span>
          <span className="hidden sm:block text-caption text-[var(--ink-3)] leading-tight">
            {multi ? t('topbar.brandCount', { count: workspaces.length }) : planLabel}
          </span>
        </span>
        <Icons.chevronDown size={16} className={['shrink-0 text-[var(--ink-3)] transition-transform', open ? 'rotate-180' : ''].join(' ')} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduced ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? undefined : { opacity: 0, y: -4 }}
            transition={motionSafe(TRANSITION.panel, reduced)}
            className="absolute start-0 top-full mt-1.5 w-[300px] bg-[var(--color-surface)] border border-[var(--line)] rounded-xl z-[60] overflow-hidden"
            style={{ boxShadow: 'var(--shadow-pop)' }}
            role="dialog"
            aria-label={t('topbar.workspaceList')}
          >
            <div className="field-ring flex items-center gap-2 px-3 h-11 border-b border-[var(--line)]">
              <Icons.search size={16} className="shrink-0 text-[var(--ink-3)]" aria-hidden="true" />
              <input
                ref={inputRef}
                id="brand-switcher-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('topbar.searchBrands')}
                aria-label={t('topbar.searchBrands')}
                className="flex-1 min-w-0 h-full bg-transparent border-none outline-none text-ui text-[var(--ink)] placeholder:text-[var(--ink-3)]"
              />
            </div>

            {pendingInvites.length > 0 && (
              <div className="border-b border-[var(--line)] py-1" data-testid="switcher-invites">
                <p className="m-0 px-3 pt-1.5 pb-1 text-caption font-semibold text-[var(--ink-3)]">{t('topbar.invitesTitle')}</p>
                {pendingInvites.map((invite) => (
                  <button
                    key={invite.id}
                    type="button"
                    onClick={() => { setOpen(false); void navigate(ROUTES.INVITES); }}
                    className="flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 border-none cursor-pointer text-start bg-transparent hover:bg-[var(--color-surface-2)]"
                  >
                    <span className="w-7 h-7 rounded-lg bg-[var(--color-accent-soft)] text-[var(--color-accent-text)] flex items-center justify-center shrink-0" aria-hidden="true">
                      <Icons.send size={16} />
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-ui font-medium text-[var(--ink)] truncate leading-tight" dir="auto">{invite.brandName}</span>
                      <span className="block text-caption text-[var(--ink-3)] leading-tight mt-0.5">
                        {t('topbar.inviteLine', { role: t(`settings.teamTab.roles.${invite.role}`) })}
                      </span>
                    </span>
                    <span className="text-caption font-semibold text-[var(--color-accent-text)] shrink-0">{t('topbar.inviteView')}</span>
                  </button>
                ))}
              </div>
            )}

            {account.known && (
              <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[var(--line)] text-meta" data-testid="switcher-account-plan">
                <span className="font-semibold text-[var(--ink-2)]">{t('topbar.accountPlan', { plan: planLabel })}</span>
                <span className="text-[var(--ink-3)] tabular-nums">
                  {t('topbar.brandsOfLimit', { used: account.brands.used, limit: account.brands.limit })}
                </span>
              </div>
            )}

            {agency && query.trim() === '' && (
              <button
                type="button"
                onClick={goAllBrands}
                className="flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 border-none border-b border-[var(--line)] cursor-pointer text-start bg-transparent hover:bg-[var(--color-surface-2)]"
              >
                <span className="w-7 h-7 rounded-lg bg-[var(--color-accent-soft)] text-[var(--color-accent-text)] flex items-center justify-center shrink-0" aria-hidden="true">
                  <Icons.grid size={16} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-ui font-medium text-[var(--ink)] leading-tight">{t('topbar.allBrands')}</span>
                  <span className="block text-caption text-[var(--ink-3)] leading-tight mt-0.5">{t('topbar.allBrandsHint')}</span>
                </span>
              </button>
            )}

            <div className="max-h-[280px] overflow-y-auto py-1" role="listbox" aria-label={t('topbar.workspaceList')} onKeyDown={(e) => moveFocusWithArrows(e, '[role="option"]')}>
              {filtered.length === 0 && <p className="px-3 py-3 m-0 text-ui text-[var(--ink-3)]">{t('topbar.noBrands')}</p>}
              {filtered.map((ws, i) => {
                const active = ws.id === currentWorkspace.id;
                const index = workspaces.indexOf(ws);
                const own = ws.ownerId === userId;
                const prev = i > 0 ? filtered[i - 1] : undefined;
                const groupStarts = hasShared && (i === 0 || (prev !== undefined && (prev.ownerId === userId) !== own));
                return (
                  <div key={ws.id}>
                  {groupStarts && (
                    <p className="m-0 px-3 pt-2 pb-1 text-caption font-semibold text-[var(--ink-3)]" role="presentation">
                      {own ? t('topbar.yourBrands') : t('topbar.sharedBrands')}
                    </p>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => handleSwitch(ws)}
                    className={[
                      'flex items-center gap-2.5 w-full min-h-[44px] px-3 py-2 border-none cursor-pointer text-start',
                      active ? 'bg-[var(--color-surface-3)]' : 'bg-transparent hover:bg-[var(--color-surface-2)]',
                    ].join(' ')}
                  >
                    <Avatar ws={ws} size={28} />
                    <span className="flex-1 min-w-0">
                      <span className="block text-ui font-medium text-[var(--ink)] truncate leading-tight">{ws.name}</span>
                    </span>
                    {active ? (
                      <Icons.check size={16} className="shrink-0 text-[var(--ink)]" aria-hidden="true" />
                    ) : (
                      index >= 0 && index < 9 && multi && (
                        <kbd className="hidden lg:inline-flex items-center px-1.5 py-0.5 rounded border border-[var(--line-2)] text-[10px] font-mono text-[var(--ink-3)]" aria-hidden="true">
                          Ctrl {index + 1}
                        </kbd>
                      )
                    )}
                  </button>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-[var(--line)]">
              <button
                type="button"
                onClick={() => { setOpen(false); setQuery(''); setAddOpen(true); }}
                className="flex items-center gap-2 w-full min-h-[44px] px-3 bg-transparent border-none cursor-pointer text-start hover:bg-[var(--color-surface-2)]"
              >
                <Icons.add size={16} className="shrink-0 text-[var(--ink-3)]" aria-hidden="true" />
                <span className="flex-1 min-w-0">
                  <span className="block text-ui font-medium text-[var(--ink-2)] leading-tight">{t('topbar.addBrand')}</span>
                  <span className="block text-caption text-[var(--ink-3)] leading-tight mt-0.5">{t('topbar.addBrandHint')}</span>
                </span>
              </button>
              <button
                type="button"
                onClick={() => { setOpen(false); void navigate(ROUTES.SETTINGS); }}
                className="flex items-center gap-2 w-full min-h-[44px] px-3 bg-transparent border-none cursor-pointer text-start hover:bg-[var(--color-surface-2)]"
              >
                <Icons.settings size={16} className="shrink-0 text-[var(--ink-3)]" aria-hidden="true" />
                <span className="text-ui font-medium text-[var(--ink-2)]">{t('topbar.workspaceSettings')}</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AddBrandSheet open={addOpen} onClose={() => setAddOpen(false)} />
    </div>
  );
}
